import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../../../../models'
import type * as segment from '../segment'
import type * as widget from '../widget'

/**
 * Represents {@link ents.core.mosaic.Page.Content} but structured to be easier
 * to consume in our Angular components
 *
 * Created using {@link Content.from}
 */
export type Content = ExactlyOneOf<{
  basic: Content.Container
  scrolling: readonly Content.Container[]
  tabbed: readonly Content.Container[]
}>
export namespace Content {
  /**
   * Creates {@link Content} from {@link ents.core.mosaic.Page.Content}
   */
  export function from(content: ents.core.mosaic.Page.Content): Content {
    if (content.root) {
      return { basic: new Container(content) }
    }
    switch (content.navigator) {
      case ents.core.mosaic.Page.Segment.Navigator.Scrolling:
        return {
          scrolling: content.segments.map(
            (segment, i) => new Container(segment, i.toString()),
          ),
        }
      case ents.core.mosaic.Page.Segment.Navigator.Tabs:
        return {
          tabbed: content.segments.map(
            (segment, i) => new Container(segment, i.toString()),
          ),
        }
    }
  }

  /**
   * Represents a {@link ents.core.mosaic.Page.Content.Container} but structured
   * to be easier to consume in our Angular components
   */
  export class Container implements Pick<segment.Segment, 'alias' | 'name'> {
    /**
     * Lookup of {@link models.widget.Ref} by {@link models.widget.Ref.Id}
     */
    private readonly _widgetRefLookup: WidgetRefLookup = {}

    public get allWidgetRefs(): readonly models.widget.Ref[] {
      return Object.values(this._widgetRefLookup).filter(
        TypeChecker.isNotNullish.raw,
      )
    }

    /**
     * If this {@link Container} corresponds to a {@link segment.Segment}, then
     * this is the {@link segment.Segment.name}. Otherwise, it is an empty string
     */
    public readonly name: string

    /**
     * If this {@link Container} corresponds to a {@link segment.Segment}, then
     * this is the {@link segment.Segment.alias}. Otherwise, it is an empty string
     */
    public readonly alias: string

    /**
     * Root column for this {@link Container}
     */
    public readonly root: Content.Element<'column'>

    constructor(
      source: ents.core.mosaic.Page.Content.Container | segment.Segment,
      id = '',
    ) {
      const {
        root,
        alias = '',
        name = '',
      } = source as FlattenUnion<typeof source>
      this.alias = alias
      this.name = name
      this.root = { column: _createColumn(root, this._widgetRefLookup, id) }
    }

    /**
     * Returns the {@link models.widget.Ref} corresponding to the given
     * {@link models.widget.Ref.Id}
     */
    public getWidgetRef(
      id: models.widget.Ref.Id,
    ): models.widget.Ref | undefined {
      return this._widgetRefLookup[id]
    }
  }

  export type Element<
    K extends keyof Element.Everything = keyof Element.Everything,
  > = ExactlyOneOf<Pick<Element.Everything, K>>
  export namespace Element {
    export interface Column {
      elements: readonly {
        height: number
        element: Element<'widget' | 'row'>
      }[]
    }

    export interface Row {
      elements: readonly {
        span: number
        element: Element<'widget' | 'column'>
      }[]
    }

    /**
     * @internal
     *
     * Used by {@link Element}. This is only exposed so that
     * TypeScript does not complain
     */
    export interface Everything {
      widget: models.widget.Ref
      column: Element.Column
      row: Element.Row
    }
  }
}

/**
 * Used to create {@link Container.root}
 */
function _createColumn(
  column: ents.core.mosaic.Page.Column,
  widgetRefLookup: WidgetRefLookup,
  _id = '',
): Content.Element.Column {
  return {
    elements: column.elements.map<Content.Element.Column['elements'][number]>(
      ({ content: { row, widget }, height }, i) => {
        const id = `${_id === undefined ? '' : `${_id}.`}${i}`
        return {
          element: widget
            ? { widget: (widgetRefLookup[id] = { ...widget, id }) }
            : { row: _createRow(row, widgetRefLookup, id) },
          height,
        }
      },
    ),
  }
}
/**
 * Used by {@link _createColumn} to create {@link Container.root}
 */
function _createRow(
  row: ents.core.mosaic.Page.Row,
  widgetRefLookup: WidgetRefLookup,
  _id = '',
): Content.Element.Row {
  return {
    elements: row.elements.map<Content.Element.Row['elements'][number]>(
      ({ content: { column, widget }, span }, i) => {
        const id = `${_id ? `${_id}.` : ''}${i}`
        return {
          element: widget
            ? { widget: (widgetRefLookup[id] = { ...widget, id }) }
            : { column: _createColumn(column, widgetRefLookup, id) },
          span,
        }
      },
    ),
  }
}

type WidgetRefLookup = Partial<Record<models.widget.Ref.Id, models.widget.Ref>>
